import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { BarChart3, Moon, Sun, History } from "lucide-react";
import { fetchHotelStats } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";

const pct = (v) => `${Math.round((v || 0) * 100)} %`;
const dayLabel = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

export const HotelStatsSection = ({ hotelId }) => {
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setStats(null);
    fetchHotelStats(hotelId, days).then(setStats).catch(() => setError("Statistiques indisponibles"));
  }, [hotelId, days]);

  if (error) return <div className="glass-card p-5 text-sm text-red-600" data-testid="hotel-stats-error">{error}</div>;
  if (!stats) return <div className="glass-card p-5 text-sm" data-testid="hotel-stats-loading">Chargement des statistiques...</div>;
  const ov = stats.off_peak_vs_peak;
  const totalRev = ov.off_peak.revenue + ov.peak.revenue || 1;

  return (
    <section className="glass-card p-5" data-testid="hotel-stats-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm font-bold"><BarChart3 size={16} className="text-orange-600" /> Statistiques · {dayLabel(stats.from)} → {dayLabel(stats.to)}</div>
        <div className="flex gap-2">
          {[7, 30].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} className={`chip ${days === d ? "active" : ""}`} data-testid={`stats-days-${d}`}>{d} jours</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Revenus" value={fmtPrice(stats.totals.revenue)} testid="stats-revenue" />
        <Kpi label="Réservations" value={stats.totals.reservations} testid="stats-reservations" />
        <Kpi label="Chambres vendues" value={stats.totals.rooms} testid="stats-rooms" />
        <Kpi label="Taux d'occupation" value={pct(stats.totals.occupancy)} testid="stats-occupancy" hint={`sur ${stats.capacity} ch. × 24 créneaux`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Revenus & occupation par jour</div>
          <div className="h-52" data-testid="stats-daily-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10 }} interval={days === 30 ? 4 : 0} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v, n, p) => (n === "revenue" ? [fmtPrice(v), "Revenus"] : [v, n])} labelFormatter={(l, p) => `${dayLabel(l)} · occupation ${pct(p?.[0]?.payload?.occupancy)}`} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {stats.daily.map((d, i) => <Cell key={i} fill={d.occupancy >= 0.5 ? "#16a34a" : d.occupancy > 0 ? "#f97316" : "#e5e7eb"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Revenus par tranche horaire</div>
          <div className="h-52" data-testid="stats-bands-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.bands} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => [fmtPrice(v), "Revenus"]} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {stats.bands.map((b, i) => <Cell key={i} fill={b.band === "nuit" ? "#1e3a8a" : b.band === "soiree" ? "#7c3aed" : "#f59e0b"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-5" data-testid="stats-offpeak-vs-peak">
        <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2">Heures creuses ({ov.off_peak_hours}) vs heures pleines</div>
        <div className="flex h-4 rounded-full overflow-hidden bg-[var(--line)]">
          <div className="bg-indigo-500" style={{ width: `${(ov.off_peak.revenue / totalRev) * 100}%` }} />
          <div className="bg-orange-500" style={{ width: `${(ov.peak.revenue / totalRev) * 100}%` }} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
          <div className="inline-flex items-center gap-2"><Moon size={14} className="text-indigo-500" /> Creuses : <b data-testid="stats-offpeak-revenue">{fmtPrice(ov.off_peak.revenue)}</b> <span className="text-xs text-[var(--ink-soft)]">({ov.off_peak.reservations} rés.)</span></div>
          <div className="inline-flex items-center gap-2"><Sun size={14} className="text-orange-500" /> Pleines : <b data-testid="stats-peak-revenue">{fmtPrice(ov.peak.revenue)}</b> <span className="text-xs text-[var(--ink-soft)]">({ov.peak.reservations} rés.)</span></div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)] mb-2"><History size={13} /> Historique des changements de prix</div>
        {stats.price_history.length === 0 ? (
          <div className="text-sm text-[var(--ink-soft)]" data-testid="stats-history-empty">Aucun ajustement Tarif Express pour l&apos;instant.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stats-history-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]"><th className="py-1.5 pr-3">Quand</th><th className="py-1.5 pr-3">Ajustement</th><th className="py-1.5 pr-3">Ancien → nouveau prix</th><th className="py-1.5 pr-3">Chambres</th><th className="py-1.5 pr-3">Origine</th><th className="py-1.5">Validité</th></tr></thead>
              <tbody>
                {stats.price_history.slice(0, 15).map((h, i) => (
                  <tr key={i} className="border-b border-[var(--line)]/60" data-testid={`stats-history-row-${i}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(h.at).toLocaleString("fr-FR")}</td>
                    <td className={`py-1.5 pr-3 font-bold ${h.pct < 0 ? "text-emerald-700" : h.pct > 0 ? "text-rose-700" : ""}`}>{h.pct > 0 ? "+" : ""}{h.pct} %</td>
                    <td className="py-1.5 pr-3">{h.old_price != null ? `${fmtPrice(h.old_price)} → ` : ""}{fmtPrice(h.new_price)}</td>
                    <td className="py-1.5 pr-3">{h.rooms_available ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-xs">{{ manual: "Lien admin", daily: "SMS 7h", low_occupancy: "Alerte 15h", test: "Test" }[h.source] || "—"}</td>
                    <td className="py-1.5 text-xs text-[var(--ink-soft)]">{h.until ? `jusqu'au ${new Date(h.until).toLocaleString("fr-FR")}` : "jusqu'à nouvel ordre"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

function Kpi({ label, value, testid, hint }) {
  return (
    <div className="rounded-2xl border border-[var(--line)] p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-[var(--ink-soft)]">{label}</div>
      <div className="text-xl font-extrabold mt-1">{value}</div>
      {hint && <div className="text-[10px] text-[var(--ink-soft)]">{hint}</div>}
    </div>
  );
}
